import { useState } from "react";

import { ComponentRepeater } from "@/components/atoms/ComponentRepeater";
import { AddEditReviewModal } from "@/components/molecules/AddEditReviewModal";
import { ApplicationRating } from "@/components/molecules/ApplicationRating";
import { AreYouSureDeleteReviewModal } from "@/components/molecules/AreYouSureDeleteReviewModal";
import { FilterOrSortSelect } from "@/components/molecules/FilterOrSortSelect";
import { SINGLE_PAGE_SECTIONS } from "@/components/molecules/NavigationBarBySection";
import { Review } from "@/components/molecules/Review";
import ReviewSkeleton from "@/components/molecules/Review/reviewSkeleton";
import { TitleSinglePageApp } from "@/components/molecules/TitleSingleAppPage";
import EmptyStateRateAndReview from "@/components/organisms/AppRateAndReview/EmptyStateRateAndReview";
import type { IAppRateAndReviewProps } from "@/components/organisms/AppRateAndReview/types";
import type {
  AddOrEditEnum,
  IReview,
} from "@/components/pages/SinglePageApp/types";
import { useTranslations } from "@/hooks/common/useTranslations";

import type { IApplicationReviewsResponse } from "@/services/apiHandlers/applications/types";

const AppRateAndReview = ({
  reviews,
  ratesCount: { ratesCount, rating },
  singleReviewState: { isShowEditOrAddModalReview, appReview },
  reviewHandlers: {
    onAddReviewHandler,
    onEditReviewHandler,
    onCloseReviewModal,
    helpfulOrUnhelpfulReviewHandler,
    deleteReviewHandler,
    createOrEditReviewHandler,
  },
  loadingStates: {
    isLoadingAppReview,
    isLoadingReviews,
    isLoadingDeleteReview,
    isLoadingCreateEditReview,
    isFetchingNextPageReviews,
  },
  mostHelpfulAndUnHelpfulReviewCounts,
  filterAndSoring: {
    currentFilter,
    transformedFiltersValue,
    onChangeFilter,
    currentSort,
    transformedSortValues,
    onChangeSort,
  },
}: IAppRateAndReviewProps) => {
  const { t } = useTranslations("SingleAppPage");
  const [isOpenDeleteReview, setIsOpenDeleteReview] = useState(false);
  const [deletedReviewId, setDeletedReviewId] = useState<number | null>(null);
  const ratingIsEqualToZeroThenItEmpty = rating === 0;

  const createOrUpdateHandler = (review: IReview, addOrEdit: AddOrEditEnum) => {
    createOrEditReviewHandler(review, addOrEdit, () => onCloseReviewModal());
  };

  const onOpenEditReviewHandler = (comment: IApplicationReviewsResponse) => {
    onEditReviewHandler(comment.id);
  };

  const onVisibleDeleteReviewHandler = () => {
    setIsOpenDeleteReview((prevState) => !prevState);
  };

  const saveDeletedReviewId = (reviewId: number) => {
    setDeletedReviewId(reviewId);
    onVisibleDeleteReviewHandler();
  };

  const deleteReview = () => {
    if (deletedReviewId !== null) {
      deleteReviewHandler(deletedReviewId, onVisibleDeleteReviewHandler);
    }
  };

  return (
    <section id={SINGLE_PAGE_SECTIONS.ratings} className="max-sm:p-4">
      {isShowEditOrAddModalReview && (
        <AddEditReviewModal
          open={isShowEditOrAddModalReview}
          createOrUpdateReview={createOrUpdateHandler}
          isLoadingCreateEditReview={isLoadingCreateEditReview}
          onClose={onCloseReviewModal}
          isLoadingAppReview={isLoadingAppReview}
          review={appReview}
        />
      )}

      {isOpenDeleteReview && (
        <AreYouSureDeleteReviewModal
          open={isOpenDeleteReview}
          deleteReview={deleteReview}
          isLoadingDelete={isLoadingDeleteReview}
          onClose={onVisibleDeleteReviewHandler}
        />
      )}

      <TitleSinglePageApp
        title={t("ratingAndReview")}
        extra={
          !ratingIsEqualToZeroThenItEmpty && (
            <div className="max-sm:hidden flex items-center gap-2">
              <FilterOrSortSelect
                data={transformedFiltersValue}
                value={currentFilter.value}
                onChange={(filterValue) => onChangeFilter(String(filterValue))}
              />
              <FilterOrSortSelect
                data={transformedSortValues}
                value={currentSort.value}
                onChange={(sortedValue) => onChangeSort(sortedValue)}
              />
            </div>
          )
        }
      />
      {ratingIsEqualToZeroThenItEmpty ? (
        <EmptyStateRateAndReview onClickWriteReview={onAddReviewHandler} />
      ) : (
        <div className="flex flex-row max-md:flex-col gap-10">
          <ApplicationRating
            rating={rating}
            ratesCount={ratesCount}
            onClickWriteReview={onAddReviewHandler}
          />
          <div className="basis-3/4">
            {!ratingIsEqualToZeroThenItEmpty && (
              <div className="hidden max-sm:flex justify-between gap-2 mb-4">
                <FilterOrSortSelect
                  data={transformedFiltersValue}
                  value={currentFilter.value}
                  onChange={(filterValue) =>
                    onChangeFilter(String(filterValue))
                  }
                />
                <FilterOrSortSelect
                  data={transformedSortValues}
                  value={currentSort.value}
                  onChange={(sortedValue) => onChangeSort(sortedValue)}
                />
              </div>
            )}

            <div className="flex flex-col gap-3">
              {isLoadingReviews ? (
                <ComponentRepeater count={4} component={<ReviewSkeleton />} />
              ) : (
                reviews.map((review, index) => {
                  const mostHelpfulOrUnhelpfulReviewPoint =
                    mostHelpfulAndUnHelpfulReviewCounts?.find(
                      ({ id }) => review.id === id,
                    )?.point;

                  return (
                    <Review
                      key={index}
                      review={review}
                      reviewPoint={mostHelpfulOrUnhelpfulReviewPoint}
                      reviewHandlers={{
                        onClickEditHandler: onOpenEditReviewHandler,
                        onClickDeleteHandler: saveDeletedReviewId,
                        helpfulOrUnhelpfulReviewHandler,
                      }}
                    />
                  );
                })
              )}
              {isFetchingNextPageReviews && (
                <ComponentRepeater count={4} component={<ReviewSkeleton />} />
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AppRateAndReview;
